import { useState, type FormEvent } from "react";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import Download from "lucide-react/dist/esm/icons/download";
import Radio from "lucide-react/dist/esm/icons/radio";
import Trophy from "lucide-react/dist/esm/icons/trophy";
import X from "lucide-react/dist/esm/icons/x";
import Zap from "lucide-react/dist/esm/icons/zap";
import type { Card, LobbyView } from "../../game";
import { readStats } from "../stats";
import type { ConnectionStatus } from "../use-game";
import { usePwaInstall } from "../use-pwa-install";
import { PlayingCard } from "./playing-card";
import { RankingSheet } from "./ranking-sheet";

interface LobbyProps {
  connection: ConnectionStatus;
  busy: boolean;
  lobby: LobbyView | null;
  seeking: boolean;
  onQuickMatch: (name: string, decks: 1 | 2) => void;
  onTakeSeek: (seekId: string, name: string) => void;
  onCancelSeek: () => void;
  onOpenRules: () => void;
}

const HERO_CARDS: Card[] = [
  { id: "hero-spades-13", rank: 13, suit: "spades" },
  { id: "hero-hearts-1", rank: 1, suit: "hearts" },
  { id: "hero-diamonds-12", rank: 12, suit: "diamonds" },
];

function storedName(): string {
  try {
    return localStorage.getItem("chin:player-name:v1") ?? "";
  } catch {
    return "";
  }
}

export function Lobby({
  connection,
  busy,
  lobby,
  seeking,
  onQuickMatch,
  onTakeSeek,
  onCancelSeek,
  onOpenRules,
}: LobbyProps) {
  const [name, setName] = useState(storedName);
  const [doubleDeck, setDoubleDeck] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);
  const [stats] = useState(readStats);
  const pwa = usePwaInstall();
  const offline = connection !== "online";
  const validName = name.trim().length >= 2;

  function updateName(value: string): void {
    const next = value.slice(0, 18);
    setName(next);
    try {
      localStorage.setItem("chin:player-name:v1", next);
    } catch {
      // Storage is an enhancement, never a blocker.
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!validName || busy || offline || seeking) return;
    onQuickMatch(name.trim(), doubleDeck ? 2 : 1);
  }

  return (
    <main className="lobby-shell">
      <header className="lobby-nav">
        <a className="wordmark" href="/" aria-label="CHIN, inicio">CHIN!</a>
        <div className="lobby-nav__actions">
          <button className="icon-text-button" type="button" onClick={() => setRankingOpen(true)}>
            <Trophy aria-hidden="true" size={19} strokeWidth={2.2} />
            <span>Ranking</span>
          </button>
          <button className="icon-text-button" type="button" onClick={onOpenRules}>
            <BookOpen aria-hidden="true" size={19} strokeWidth={2.2} />
            <span>Cómo jugar</span>
          </button>
        </div>
      </header>

      <section className="lobby-hero" aria-labelledby="hero-title">
        <div className="lobby-copy">
          <p className="eyebrow"><Radio aria-hidden="true" size={15} /> 1 vs 1 · en tiempo real</p>
          <h1 id="hero-title">Pensá rápido.<br /><em>Jugá más rápido.</em></h1>
          <p className="lobby-lead">Cartas consecutivas, reflejos y un solo grito que cambia la mesa.</p>
        </div>

        <div className="hero-cards" aria-hidden="true">
          {HERO_CARDS.map((card, index) => (
            <div className={`hero-card hero-card--${index + 1}`} key={card.id}>
              <PlayingCard card={card} size="center" />
            </div>
          ))}
          <span className="hero-burst">¡CHIN!</span>
        </div>

        <form className="lobby-panel" onSubmit={submit}>
          <label className="field-label" htmlFor="player-name">Tu nombre</label>
          <input
            id="player-name"
            className="text-field"
            value={name}
            onChange={(event) => updateName(event.target.value)}
            placeholder="Ej. Mica"
            autoComplete="nickname"
            enterKeyHint="go"
            maxLength={18}
            required
          />

          {seeking ? (
            <div className="seek-waiting" role="status" aria-live="polite">
              <span className="seek-waiting__spinner" aria-hidden="true" />
              <span>
                <strong>Esperando rival…</strong>
                <small>Sos visible para otros jugadores</small>
              </span>
              <button type="button" className="text-button" onClick={onCancelSeek}>Cancelar</button>
            </div>
          ) : (
            <button
              className="quick-button"
              type="submit"
              disabled={busy || offline || !validName}
            >
              <Zap aria-hidden="true" size={18} />
              {busy ? "Preparando…" : offline ? "Conectando…" : "Jugar ya"}
            </button>
          )}

          <label className="deck-toggle">
            <input
              type="checkbox"
              checked={doubleDeck}
              onChange={(event) => setDoubleDeck(event.target.checked)}
            />
            <span>
              <strong>Partida larga: 2 mazos</strong>
              <small>104 cartas en juego, ideal para series</small>
            </span>
          </label>

          {!seeking && (lobby?.seeks.length ?? 0) > 0 ? (
            <div className="seek-list" aria-label="Mesas abiertas">
              <span className="seek-list__title">Mesas abiertas</span>
              {lobby!.seeks.map((seek) => (
                <div className="seek-row" key={seek.id}>
                  <span className="seek-row__name">
                    {seek.name}
                    {seek.rating !== null ? <small> · {seek.rating}</small> : null}
                    {seek.decks === 2 ? <i className="deck-badge">2 mazos</i> : null}
                  </span>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={busy || offline || !validName}
                    onClick={() => onTakeSeek(seek.id, name.trim())}
                  >
                    Jugar
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </form>
      </section>

      {stats.matches > 0 ? (
        <section className="lobby-stats" aria-label="Tus estadísticas">
          <span><b>{stats.wins}</b> victorias</span>
          <span><b>{stats.matches}</b> partidas</span>
          <span><b>{stats.chins}</b> CHIN</span>
          <span><b>{stats.bestStreak}</b> mejor racha</span>
        </section>
      ) : null}

      {pwa.canInstall ? (
        <button className="install-chip" type="button" onClick={() => void pwa.install()}>
          <Download aria-hidden="true" size={17} />
          Instalar CHIN en tu dispositivo
        </button>
      ) : pwa.iosHintVisible ? (
        <div className="install-hint" role="note">
          <Download aria-hidden="true" size={17} />
          <span>Instalá CHIN: tocá <b>Compartir</b> y después <b>"Agregar a inicio"</b></span>
          <button type="button" onClick={pwa.dismissIosHint} aria-label="Ocultar sugerencia">
            <X aria-hidden="true" size={16} />
          </button>
        </div>
      ) : null}

      <footer className="lobby-footer">
        <span className={`connection-dot connection-dot--${connection}`} />
        {connection !== "online"
          ? "Buscando servidor"
          : lobby
            ? `${lobby.online} ${lobby.online === 1 ? "jugador conectado" : "jugadores conectados"}`
            : "Servidor listo"}
        <span aria-hidden="true">·</span>
        <span>Hecho para tocar, no esperar</span>
      </footer>

      <RankingSheet open={rankingOpen} onClose={() => setRankingOpen(false)} />
    </main>
  );
}
